import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';

import { SelectedProduct } from './ProductWrapper';
import AddToCartModal, { ADD_TO_CART_STATE } from './AddToCartModal';
import ProductPrice from 'components/ProductPageV2/ProductDetails/ProductPrice/ProductPrice';
import ProductColors from 'components/ProductPageV2/ProductDetails/ProductColors/ProductColors';
import SelectRender from 'components/Checkout/Cart/ProductSheetModal/Select/SelectRender';
import ProductSizes from 'components/ProductPageV2/ProductDetails/ProductSizes/ProductSizes';
import ProductAddBasket, {
  ProductAddBasketClasses,
} from 'components/ProductPageV2/ProductDetails/ProductAddBasket/ProductAddBasket';
import ProductQuantity from 'components/ProductPageV2/ProductDetails/ProductQuantity/ProductQuantity';
import Image from 'components/Common/Medias/Image';
import Button from 'components/UI/Buttons/Button/Button';
import { useProductSku } from 'hooks/ProductPageV2/useProductSku';
import { useProductStocks } from 'hooks/ProductPageV2/useProductStocks';
import { apiUrls } from 'services/api-urls';
import {
  getBrandName,
  getColor,
  getGoToProduct,
  getLot,
  getName,
  getRealSku,
} from 'services/product';
import { getUri } from 'services/generic';
import { DESKTOP_MIN } from 'settings/device';
import { FormattedProductDataType } from 'types/ProductPageV2/Products';

import styles from './Product.module.scss';

type Props = {
  formattedProduct: FormattedProductDataType;
  color?: string;
  setColor: (color: string) => void;
  setSelectedProduct: (product: SelectedProduct) => void;
};

export default function Product({
  color,
  setColor,
  setSelectedProduct,
  formattedProduct: {
    product,
    variants,
    sizes,
    cups,
    productAttributes,
    colors,
    variantsAttributes,
    productType,
  },
}: Props): JSX.Element {
  const { t } = useTranslation('productPage');
  const [size, setSize] = useState<string>();
  const [cup, setCup] = useState<string>();
  const [qty, setQty] = useState(1);
  const [isSizeModalOpen, setSizeModalOpen] = useState(false);
  const [addToCartState, setAddToCartState] = useState<ADD_TO_CART_STATE>(
    ADD_TO_CART_STATE.NONE
  );

  const isDesktop = useMediaQuery({ minWidth: DESKTOP_MIN });
  const { imgFacePivot, codeCouleur } = productAttributes || {};
  const title = getName(productAttributes);
  const brandName = getBrandName(productAttributes);
  const baseColor = getColor(productAttributes);
  const {
    stocks,
    getStockStatusBySKU,
    isLoaded: stockIsLoaded,
  } = useProductStocks(codeCouleur.Value);

  const { selectedProduct } = useProductSku(
    product,
    variantsAttributes || {},
    productType,
    size,
    cup
  );

  const productUrl = useMemo(
    () => getGoToProduct(product, variants, selectedProduct, color),
    [selectedProduct, color, variants, product]
  );

  useEffect(() => {
    if (selectedProduct && stocks) {
      setSelectedProduct({
        ...selectedProduct,
        qty,
        stockState: getStockStatusBySKU(
          getRealSku(selectedProduct.Attributes),
          stocks
        ),
      });
    }
  }, [selectedProduct, qty, stocks]);

  function renderAddBasket(
    onSizeRequired?: boolean,
    classes?: Partial<ProductAddBasketClasses>
  ) {
    return (
      <ProductAddBasket
        product={selectedProduct}
        qty={qty}
        onSizeRequired={onSizeRequired}
        openModal={idModal => {
          if (idModal === 'productSize') {
            setSizeModalOpen(true);
          } else if (idModal === 'addToMyBasket') {
            setAddToCartState(ADD_TO_CART_STATE.SUCCESS);
          } else if (idModal === 'error') {
            setAddToCartState(ADD_TO_CART_STATE.ERROR);
          }
        }}
        closeModal={() => setSizeModalOpen(false)}
        gtmMandatoryAttributes={[]}
        classes={classes}
      />
    );
  }

  function renderProductDetails() {
    return (
      <>
        <ProductColors
          classes={{ container: styles.colorsContainer }}
          colors={colors}
          color={color ?? productAttributes.couleur.Value}
          setColor={(color, e: SyntheticEvent) => {
            e.preventDefault(); // Disable redirect
            setColor(color);
          }}
          maxItems={4}
          breakpoints={{ 1200: 5 }}
        />
        <ProductSizes
          skus={variantsAttributes || {}}
          type={productType}
          sizes={sizes}
          cups={cups}
          size={size}
          setSize={size => setSize(size)}
          cup={cup}
          setCup={setCup}
          open={isSizeModalOpen}
          closeModal={() => setSizeModalOpen(false)}
          renderAddBasket={renderAddBasket}
          classes={{ container: styles.productSizes }}
          stocks={stocks}
          stockIsLoaded={stockIsLoaded}
          SelectRenderComponent={SelectRender}
        />
        <div className={styles.qtyBasket}>
          <ProductQuantity
            qty={qty}
            setQty={setQty}
            classes={{ container: styles.qty }}
          />
          <div className={styles.basket}>
            {renderAddBasket(true, { addToCartButton: styles.basketButton })}
          </div>
        </div>
      </>
    );
  }

  function renderViewProductButton() {
    return (
      <div className={styles.viewProductWrapper}>
        <Button
          component={Link}
          to={getUri(productUrl)}
          className={styles.viewProductLink}
        >
          <span>{t('VIEW_PRODUCT')}</span>
          <ArrowForwardIosIcon className={styles.icon} />
        </Button>
      </div>
    );
  }

  return (
    <div className={classNames(styles.card, styles.productCard)}>
      <h2 className={classNames(styles.productTitle)}>
        {brandName ? `${brandName}® - ${title}` : title}
        <span className={styles.titleColor}>{baseColor}</span>
      </h2>
      <div className={styles.flexContainer}>
        <div className={styles.left}>
          {imgFacePivot && (
            <Image
              src={apiUrls.getPictureUrl(imgFacePivot.Value)}
              alt={getName(productAttributes)}
            />
          )}
          {isDesktop && renderViewProductButton()}
        </div>
        <div className={styles.right}>
          <ProductPrice
            price={
              selectedProduct?.Attributes.prix.Value ??
              productAttributes.prix?.Value
            }
            preDiscountPrice={
              selectedProduct?.Attributes.prixBarre.Value ??
              productAttributes.prixBarre?.Value
            }
            discountPercentage={
              selectedProduct?.Attributes.decote.Value ??
              productAttributes.decote?.Value
            }
            selectedSize={size}
            ecopart={productAttributes.ecopart?.Value}
            priceVolume={productAttributes.prixVolume?.Value}
            isUniquePrice={productAttributes.prixUnique.Value == '1'}
            redPrice={productAttributes.PRIXROUGE?.Value}
            liteMode={false}
            lot={getLot(productAttributes)}
            classes={{
              container: styles.priceContainer,
              productPriceContainer: styles.productPriceContainer,
              finalPriceContainer: styles.finalPriceContainer,
              price: styles.price,
            }}
          />
          {isDesktop && renderProductDetails()}
        </div>
      </div>
      {!isDesktop && (
        <div className={styles.full}>
          {renderProductDetails()}
          {renderViewProductButton()}
        </div>
      )}
      {selectedProduct && addToCartState !== ADD_TO_CART_STATE.NONE && (
        <AddToCartModal
          product={selectedProduct}
          state={addToCartState}
          updateState={state => setAddToCartState(state)}
        />
      )}
    </div>
  );
}
